import haversine from 'haversine-distance'

import { CmsSellerFilters, CustomCmsSeller } from '../../../domain/seller'

import { SUNDAY, MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY, SATURDAY } from './constants'
import { DAY_OPENING_HOURS, OPENING_HOURS } from './types'

export const getCurrentOpeningHoursDay = (openingHours: OPENING_HOURS): DAY_OPENING_HOURS[] => {
  const daysFromOpeningHours = [SUNDAY, MONDAY, TUESDAY, WEDNESDAY, THURSDAY, FRIDAY, SATURDAY]
  const day = new Date().getDay()

  const dayOfWeek = daysFromOpeningHours[day] ?? SUNDAY

  return openingHours[dayOfWeek]
}

export const parseOpeningHours = (seller: CustomCmsSeller): OPENING_HOURS | null => {
  const { openingHours } = seller

  try {
    const parsedOpeningHours: OPENING_HOURS = JSON.parse(openingHours)
    return parsedOpeningHours
  } catch (error) {
    return null
  }
}

export const isAlwaysClosed = (openingHours: OPENING_HOURS) =>
  Object.values(openingHours).every((day) => !day.length)

export const buildHoursOfDay = (
  openingHoursOfDay: DAY_OPENING_HOURS[] | undefined,
  t: (str: string) => string,
) => {
  if (!openingHoursOfDay?.length) {
    return t('Fermé')
  }

  return openingHoursOfDay.map((slot) => `${slot.start_time} - ${slot.end_time}`).join(', ')
}

export const calculateDistance = (seller: CustomCmsSeller, filters: CmsSellerFilters) => {
  if (!filters.radius?.lng || !filters.radius.lat) {
    return null
  }

  const distanceInMeters = haversine(
    { lat: filters.radius.lat, lng: filters.radius?.lng },
    { lat: Number(seller.latitude), lng: Number(seller.longitude) },
  )

  const distanceInKm = distanceInMeters / 1000

  return distanceInKm.toFixed(1)
}

export const buildLinearGradientFromPercent = (
  percent: number,
  direction: 'to right' | 'to left' = 'to right',
) => `linear-gradient(
  ${direction},
  #E5E5E5 0%,
  #E5E5E5 0%,
  #000 0%,
  #000 ${percent}%, 
  #E5E5E5 ${percent}%, 
  #E5E5E5 100%)`

export const setInfoWindowContent = (seller: CustomCmsSeller) => {
  let descriptionContent = ''
  if (seller.description !== undefined) {
    descriptionContent = `<div class="info-window-store-content-description">${seller.description}</div>`
  }

  const content = `
    <div class="info-window-store-content">
      <h5 class="info-window-store-content-title title-h5">
        <a href="${seller.url}" target="_blank">${seller.name}</a>
      </h5>
      <div class="info-window-store-content-address">
        ${seller.address}
      </div>
      ${
        seller.phone &&
        `<p class="info-window-store-content-phone">
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
            <title>icon-phone</title>
            <path d="M7.534 4.38c0.561-0.028 1.129 0.008 1.615 0.061 1.173 0.128 2.069 0.961 2.456 1.99l0.915 2.435c0.214 0.569 0.268 1.186 0.157 1.784l-0.468 2.51c-0.166 0.891-0.090 1.762 0.309 2.465 0.879 1.548 2.116 2.755 3.856 3.829 0.833 0.514 1.869 0.647 2.895 0.448l2.219-0.43c0.61-0.118 1.24-0.064 1.821 0.157l2.798 1.066c0.759 0.289 1.487 0.885 1.723 1.8 0.233 0.907 0.404 2.378-0.43 3.684-1.319 2.064-3.64 3.032-6.208 2.473-3.574-0.779-7.832-2.35-12.329-6.847-4.332-4.332-5.030-8.453-5.521-11.705-0.36-2.383 0.73-4.563 2.54-5.375 0.508-0.228 1.099-0.318 1.652-0.345zM7.599 5.712c-0.476 0.023-0.88 0.099-1.172 0.23-1.164 0.522-2.055 2.052-1.767 3.959 0.478 3.167 1.121 6.937 5.146 10.961 4.279 4.279 8.288 5.75 11.671 6.487 2.044 0.445 3.794-0.313 4.8-1.888 0.537-0.84 0.462-1.859 0.262-2.634-0.097-0.376-0.42-0.701-0.906-0.886l-2.798-1.066c-0.348-0.133-0.727-0.165-1.093-0.095l-2.219 0.43c-1.287 0.249-2.677 0.101-3.849-0.622-1.892-1.167-3.306-2.526-4.316-4.305-0.599-1.055-0.664-2.271-0.46-3.368l0.468-2.51c0.067-0.359 0.034-0.729-0.094-1.070l-0.915-2.435c-0.239-0.636-0.757-1.068-1.353-1.134-0.444-0.049-0.937-0.078-1.404-0.055z"></path>
          </svg>
          <span>${seller.phone}</span>
        </p>`
      }
      ${descriptionContent}
    </div>
  `

  return content
}

export const scrollToSeller = (seller: CustomCmsSeller) => {
  let listSellersEl = document.querySelector<HTMLElement>('.sellers-page-bloc-filters')

  if (window.matchMedia('(max-width:959px)').matches) {
    listSellersEl = document.querySelector('.map-list-sellers-ctn')
  }

  const sellerItemEl = document.querySelector<HTMLElement>(`[data-seller-id="${seller.id}"]`)

  if (!listSellersEl || !sellerItemEl) {
    return
  }

  listSellersEl.scroll({
    top: sellerItemEl.offsetTop - listSellersEl.offsetTop - 16,
    left: 0,
    behavior: 'smooth',
  })
}

export const convertMilesToKm = (value: number) => {
  const CONVERSION_FROM_MILES_TO_KM = 0.621371

  return value * CONVERSION_FROM_MILES_TO_KM
}

export default {
  buildHoursOfDay,
  buildLinearGradientFromPercent,
  calculateDistance,
  getCurrentOpeningHoursDay,
  isAlwaysClosed,
  parseOpeningHours,
  setInfoWindowContent,
}
